import React, { useState } from 'react';
import { useStore } from '../store/useStore';
import {
  createDiscipline, updateDiscipline, deleteDiscipline, reorderDisciplines,
  updateVersionSet, deleteVersionSet,
  getDisciplines, getVersionSets, getSheets,
} from '../utils/api';
import toast from 'react-hot-toast';
import styles from './DrawingsSettings.module.css';

export default function DrawingsSettings({ onClose }) {
  const {
    project, disciplines, setDisciplines,
    versionSets, setVersionSets, setSheets,
  } = useStore();

  const [tab, setTab] = useState('disciplines');

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>DRAWINGS SETTINGS</span>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${tab === 'disciplines' ? styles.tabActive : ''}`}
            onClick={() => setTab('disciplines')}
          >Disciplines</button>
          <button
            className={`${styles.tab} ${tab === 'versionSets' ? styles.tabActive : ''}`}
            onClick={() => setTab('versionSets')}
          >Version Sets</button>
        </div>

        <div className={styles.body}>
          {tab === 'disciplines' && (
            <DisciplinesTab
              project={project}
              disciplines={disciplines}
              setDisciplines={setDisciplines}
              setSheets={setSheets}
            />
          )}
          {tab === 'versionSets' && (
            <VersionSetsTab
              project={project}
              versionSets={versionSets}
              setVersionSets={setVersionSets}
              setSheets={setSheets}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Disciplines Tab ───────────────────────────────────────────────────────────
function DisciplinesTab({ project, disciplines, setDisciplines, setSheets }) {
  const [editId,    setEditId]    = useState(null); // id | 'new' | null
  const [editForm,  setEditForm]  = useState({ designator: '', name: '' });
  const [saving,    setSaving]    = useState(false);
  const [deleteId,  setDeleteId]  = useState(null);

  // ── Drag & drop reorder state ──
  // dragId: id of the row currently being dragged (null when idle)
  // dropTarget: { id, position: 'before' | 'after' } — visualised + used on drop
  const [dragId,     setDragId]     = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  function handleDragStart(e, id) {
    // Only allow drag when nothing is being edited / deleted.
    if (editId !== null || deleteId !== null) { e.preventDefault(); return; }
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Use the full row as the drag ghost, not just the handle cell.
    const tr = e.currentTarget.closest('tr');
    if (tr && e.dataTransfer.setDragImage) {
      e.dataTransfer.setDragImage(tr, 16, tr.offsetHeight / 2);
    }
  }
  function handleDragOver(e, targetId) {
    if (!dragId || dragId === targetId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const position = (e.clientY - rect.top) < rect.height / 2 ? 'before' : 'after';
    if (!dropTarget || dropTarget.id !== targetId || dropTarget.position !== position) {
      setDropTarget({ id: targetId, position });
    }
  }
  function handleDragEnd() {
    setDragId(null);
    setDropTarget(null);
  }
  async function handleDrop(e) {
    e.preventDefault();
    if (!dragId || !dropTarget) { handleDragEnd(); return; }

    const fromIdx = disciplines.findIndex((d) => d.id === dragId);
    const toIdx   = disciplines.findIndex((d) => d.id === dropTarget.id);
    handleDragEnd();
    if (fromIdx < 0 || toIdx < 0) return;

    let insertIdx = dropTarget.position === 'before' ? toIdx : toIdx + 1;
    // Removing fromIdx first shifts everything after it up by 1.
    if (fromIdx < insertIdx) insertIdx -= 1;
    if (insertIdx === fromIdx) return; // no-op

    const next = [...disciplines];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(insertIdx, 0, moved);

    // Optimistic local reorder; server reply reconciles.
    setDisciplines(next);
    try {
      const saved = await reorderDisciplines(project.id, next.map((d) => d.id));
      setDisciplines(saved);
      // Sheets are server-sorted by discipline.sort_order — refetch to pick
      // up the new ordering in the drawings list view.
      const updatedSheets = await getSheets({ project_id: project.id });
      setSheets(updatedSheets);
    } catch (err) {
      toast.error(err.message || 'Reorder failed');
      // Revert on failure.
      setDisciplines(disciplines);
    }
  }

  function startEdit(disc) {
    setEditId(disc.id);
    setEditForm({ designator: disc.designator, name: disc.name });
  }
  function startNew() {
    setEditId('new');
    setEditForm({ designator: '', name: '' });
  }
  function cancelEdit() { setEditId(null); }

  async function saveEdit() {
    if (!editForm.designator.trim() || !editForm.name.trim()) {
      toast.error('Designator and name are required');
      return;
    }
    setSaving(true);
    try {
      if (editId === 'new') {
        await createDiscipline({
          project_id: project.id,
          designator: editForm.designator.trim().toUpperCase(),
          name: editForm.name.trim(),
        });
      } else {
        await updateDiscipline(editId, {
          designator: editForm.designator.trim().toUpperCase(),
          name: editForm.name.trim(),
        });
      }
      const updated = await getDisciplines(project.id);
      setDisciplines(updated);
      setEditId(null);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(id) {
    setSaving(true);
    try {
      await deleteDiscipline(id);
      const updated = await getDisciplines(project.id);
      setDisciplines(updated);
      // Sheets just lose their discipline (FK ON DELETE SET NULL)
      const updatedSheets = await getSheets({ project_id: project.id });
      setSheets(updatedSheets);
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setSaving(false);
      setDeleteId(null);
    }
  }

  return (
    <div className={styles.tabContent}>
      <p className={styles.tabHint}>
        Disciplines organise sheets by their prefix designator (e.g. A = Architecture).
        Deleting a discipline does <strong>not</strong> delete sheets — they just become unassigned.
      </p>

      <table className={styles.settingsTable}>
        <thead>
          <tr>
            <th className={styles.dragHeadCell} />
            <th>DESIGNATOR</th>
            <th>NAME</th>
            <th>SHEETS</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {disciplines.map((d) => {
            const isDragging = dragId === d.id;
            const dropBefore = dropTarget?.id === d.id && dropTarget.position === 'before';
            const dropAfter  = dropTarget?.id === d.id && dropTarget.position === 'after';
            const rowCls = [
              isDragging ? styles.rowDragging : '',
              dropBefore ? styles.rowDropBefore : '',
              dropAfter  ? styles.rowDropAfter  : '',
            ].filter(Boolean).join(' ');
            const canDrag = editId === null && deleteId === null;
            return (
              <tr
                key={d.id}
                className={rowCls}
                onDragOver={(e) => handleDragOver(e, d.id)}
                onDrop={handleDrop}
              >
                {editId === d.id ? (
                  <>
                    <td className={styles.dragHandleCell} aria-hidden="true" />
                    <td>
                      <input
                        className={styles.inlineInput}
                        value={editForm.designator}
                        onChange={(e) => setEditForm((f) => ({ ...f, designator: e.target.value }))}
                        placeholder="A"
                        style={{ width: 60 }}
                      />
                    </td>
                    <td>
                      <input
                        className={styles.inlineInput}
                        value={editForm.name}
                        onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Architecture"
                      />
                    </td>
                    <td />
                    <td>
                      <div className={styles.rowActions}>
                        <button className={styles.saveRowBtn} onClick={saveEdit} disabled={saving}>
                          {saving ? '…' : 'Save'}
                        </button>
                        <button className={styles.cancelRowBtn} onClick={cancelEdit}>Cancel</button>
                      </div>
                    </td>
                  </>
                ) : deleteId === d.id ? (
                  <>
                    <td className={styles.dragHandleCell} aria-hidden="true" />
                    <td colSpan={3} className={styles.deleteConfirmText}>
                      Delete <strong>{d.designator}</strong> — {d.name}?
                      Sheets will be unassigned.
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className={styles.deleteRowBtn} onClick={() => confirmDelete(d.id)} disabled={saving}>
                          {saving ? '…' : 'Delete'}
                        </button>
                        <button className={styles.cancelRowBtn} onClick={() => setDeleteId(null)}>Cancel</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td
                      className={styles.dragHandleCell}
                      draggable={canDrag}
                      onDragStart={(e) => handleDragStart(e, d.id)}
                      onDragEnd={handleDragEnd}
                      title="Drag to reorder"
                    >
                      <GripIcon />
                    </td>
                    <td><span className={styles.designatorBadge}>{d.designator}</span></td>
                    <td>{d.name}</td>
                    <td className={styles.countCell}>{d.sheet_count ?? '—'}</td>
                    <td>
                      <div className={styles.rowActions}>
                        <button className={styles.editRowBtn} onClick={() => startEdit(d)}>Edit</button>
                        <button className={styles.deleteIconBtn} onClick={() => setDeleteId(d.id)}>
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            );
          })}

          {editId === 'new' && (
            <tr className={styles.newRow}>
              <td className={styles.dragHandleCell} aria-hidden="true" />
              <td>
                <input
                  className={styles.inlineInput}
                  value={editForm.designator}
                  onChange={(e) => setEditForm((f) => ({ ...f, designator: e.target.value }))}
                  placeholder="S"
                  style={{ width: 60 }}
                  autoFocus
                />
              </td>
              <td>
                <input
                  className={styles.inlineInput}
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Structural"
                />
              </td>
              <td />
              <td>
                <div className={styles.rowActions}>
                  <button className={styles.saveRowBtn} onClick={saveEdit} disabled={saving}>
                    {saving ? '…' : 'Add'}
                  </button>
                  <button className={styles.cancelRowBtn} onClick={cancelEdit}>Cancel</button>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {editId !== 'new' && (
        <button className={styles.addRowBtn} onClick={startNew}>+ Add Discipline</button>
      )}
    </div>
  );
}

// ── Version Sets Tab ──────────────────────────────────────────────────────────
function VersionSetsTab({ project, versionSets, setVersionSets, setSheets }) {
  const [editId,   setEditId]   = useState(null);
  const [editName, setEditName] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [cascade,  setCascade]  = useState(false);

  function startEdit(vs) {
    setEditId(vs.id);
    setEditName(vs.name);
  }
  function cancelEdit() { setEditId(null); }

  async function saveEdit() {
    if (!editName.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await updateVersionSet(editId, { name: editName.trim() });
      const updated = await getVersionSets(project.id);
      setVersionSets(updated);
      setEditId(null);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    setSaving(true);
    try {
      await deleteVersionSet(deleteId, cascade);
      const [updatedVS, updatedSheets] = await Promise.all([
        getVersionSets(project.id),
        getSheets({ project_id: project.id }),
      ]);
      setVersionSets(updatedVS);
      setSheets(updatedSheets);
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setSaving(false);
      setDeleteId(null);
      setCascade(false);
    }
  }

  const deleteTarget = versionSets.find((v) => v.id === deleteId);

  return (
    <div className={styles.tabContent}>
      <p className={styles.tabHint}>
        Version sets group drawings issued at a point in time (e.g. CD-001, IFC Rev A).
        You can rename them or delete them — with the option to also remove all associated sheets.
      </p>

      <table className={styles.settingsTable}>
        <thead>
          <tr>
            <th>NAME</th>
            <th>SHEETS</th>
            <th>CREATED</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {versionSets.length === 0 && (
            <tr>
              <td colSpan={4} className={styles.emptyRow}>No version sets yet</td>
            </tr>
          )}
          {versionSets.map((vs) => (
            <tr key={vs.id}>
              {editId === vs.id ? (
                <>
                  <td>
                    <input
                      className={styles.inlineInput}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                  </td>
                  <td />
                  <td />
                  <td>
                    <div className={styles.rowActions}>
                      <button className={styles.saveRowBtn} onClick={saveEdit} disabled={saving}>
                        {saving ? '…' : 'Save'}
                      </button>
                      <button className={styles.cancelRowBtn} onClick={cancelEdit}>Cancel</button>
                    </div>
                  </td>
                </>
              ) : (
                <>
                  <td className={styles.vsNameCell}>{vs.name}</td>
                  <td className={styles.countCell}>{vs.sheet_count ?? 0}</td>
                  <td className={styles.dateCell}>
                    {vs.created_at ? new Date(vs.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <button className={styles.editRowBtn} onClick={() => startEdit(vs)}>Rename</button>
                      <button className={styles.deleteIconBtn} onClick={() => { setDeleteId(vs.id); setCascade(false); }}>
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Delete confirmation panel */}
      {deleteId && deleteTarget && (
        <div className={styles.deletePanel}>
          <p className={styles.deletePanelTitle}>
            Delete <strong>{deleteTarget.name}</strong>?
          </p>
          {(deleteTarget.sheet_count || 0) > 0 && (
            <label className={styles.cascadeLabel}>
              <input
                type="checkbox"
                checked={cascade}
                onChange={(e) => setCascade(e.target.checked)}
              />
              Also delete all {deleteTarget.sheet_count} associated sheet{deleteTarget.sheet_count !== 1 ? 's' : ''}{' '}
              <span className={styles.cascadeWarn}>(cannot be undone)</span>
            </label>
          )}
          {(deleteTarget.sheet_count || 0) > 0 && !cascade && (
            <p className={styles.deletePanelNote}>
              Sheets will remain but will no longer be assigned to a version set.
            </p>
          )}
          <div className={styles.deletePanelActions}>
            <button className={styles.deleteRowBtn} onClick={confirmDelete} disabled={saving}>
              {saving ? '…' : `Delete${cascade ? ' & sheets' : ''}`}
            </button>
            <button className={styles.cancelRowBtn} onClick={() => { setDeleteId(null); setCascade(false); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const TrashIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3a1 1 0 011-1h2a1 1 0 011 1v1M5 4l.75 8.5a1 1 0 001 .9h2.5a1 1 0 001-.9L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const GripIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
    <circle cx="3" cy="3"  r="1" fill="currentColor"/>
    <circle cx="7" cy="3"  r="1" fill="currentColor"/>
    <circle cx="3" cy="7"  r="1" fill="currentColor"/>
    <circle cx="7" cy="7"  r="1" fill="currentColor"/>
    <circle cx="3" cy="11" r="1" fill="currentColor"/>
    <circle cx="7" cy="11" r="1" fill="currentColor"/>
  </svg>
);
